import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, Spinner, Alert } from 'reactstrap'
import { fetchBlogPost, fetchBlogPosts, fetchRecentBlogPosts } from '../../helpers/blogApi'
import SEO from '../../components/Common/SEO'
import { generateCanonicalUrl } from '../../utils/seoUtils'
import { blockRenderer } from '../../utils/blockRenderer'
import ReactMarkdown from 'react-markdown' // In case the custom API returns markdown
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics'

const BlogDetail = ({ previewData }) => {
    const { slug } = useParams()
    const { trackEvent } = useAnalytics()
    const [post, setPost] = useState(previewData || null)
    const [loading, setLoading] = useState(!previewData)
    const [error, setError] = useState(null)
    const [recentPosts, setRecentPosts] = useState([])

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim()
    }

    useEffect(() => {
        if (!previewData) {
            loadPost()
        } else {
            setPost(previewData)
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, previewData])

    // Fetch the latest blogs section once the main post is loaded
    useEffect(() => {
        if (!post) return
        loadRecentPosts(post)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post?.id])

    const loadPost = async () => {
        try {
            setLoading(true)
            const postsResponse = await fetchBlogPosts({ page: 1, pageSize: 100 })
            const allPosts = postsResponse.data
            const matchingPost = allPosts.find(p => (p.slug || generateSlug(p.title)) === slug)
            if (!matchingPost) {
                setPost(null)
                setError('Blog post not found.')
            } else {
                const data = await fetchBlogPost(matchingPost.id)
                const loadedPost = data || matchingPost
                setPost(loadedPost)
                setError(null)
                const loadedCategory = loadedPost.category || {}
                trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_POST_VIEWED, {
                    post_title: loadedPost.title,
                    post_slug: loadedPost.slug || slug,
                    category: loadedCategory?.name || loadedCategory,
                })
            }
        } catch (err) {
            console.error('Error loading blog post:', err)
            setError('Blog post not found.')
            setPost(null)
        } finally {
            setLoading(false)
        }
    }

    const loadRecentPosts = async (currentPost) => {
        try {
            const response = await fetchRecentBlogPosts(currentPost.id)
            // Slugs the API sends back (this post's own `slug`, and each recent
            // post's `slug`) keep raw title punctuation (apostrophes, colons...),
            // which doesn't match this page's own generateSlug(title) URLs - the
            // only slug the main blog list and loadPost() above ever use, since
            // the list endpoint doesn't return a `slug` field at all. Comparing
            // and linking by the same generateSlug(title) here keeps a "Latest
            // Blogs" card landing on the same URL a click from the main list would.
            const currentSlug = generateSlug(currentPost.title || '')
            const filtered = response.data
                .filter(p => generateSlug(p.title || '') !== currentSlug)
                .slice(0, 3)
            setRecentPosts(filtered)
        } catch (e) {
            // silently skip — the latest-blogs section is non-critical
        }
    }

    const getImageUrl = (p) => {
        if (!p) return null
        return p.cover || p.cover_image || p.image || p.thumbnail || p.cover?.url || null
    }

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const getDescription = (p) => {
        return p.meta_description || p.description || p.excerpt || p.summary || 'Read this article on KeyQuest.'
    }

    const getExcerpt = (p) => {
        const text = p.description || p.excerpt || p.summary || ''
        return text.length > 100 ? text.substring(0, 100) + '…' : text
    }

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-3">Loading blog post...</p>
                    </div>
                </Container>
            </div>
        )
    }

    if (error || !post) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Alert color="danger">
                        {error || 'Blog post not found'}
                    </Alert>
                    <Link to="/blog" className="btn btn-primary">
                        ← Back to Blog
                    </Link>
                </Container>
            </div>
        )
    }

    const author = post.author || {}
    const category = post.category || {}
    const seo = post.seo || {}

    const canonicalUrl = generateCanonicalUrl(`/blog/${slug}`)
    const title = seo.metaTitle || post.title || 'Blog Post'
    const description = seo.metaDescription || getDescription(post)
    const robots = previewData ? 'noindex, follow' : 'index, follow'

    const publishedAt = post.published_at || post.publishedAt || post.created_at || post.create_time
    const updatedAt = post.update_time || post.updatedAt || publishedAt

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: description,
        image: getImageUrl(post) ? getImageUrl(post) : `${window.location.origin}/new_web_logo.png`,
        datePublished: publishedAt,
        dateModified: updatedAt,
        author: {
            '@type': 'Person',
            name: author?.name || 'KeyQuest Team'
        },
        publisher: {
            '@type': 'Organization',
            name: 'KeyQuest',
            logo: {
                '@type': 'ImageObject',
                url: `${window.location.origin}/new_web_logo.png`
            }
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl
        }
    }

    return (
        <React.Fragment>
            <SEO
                title={title}
                description={description}
                canonical={canonicalUrl}
                robots={robots}
                type="article"
                author={author?.name}
                publishedTime={publishedAt}
                modifiedTime={updatedAt}
                image={getImageUrl(post)}
                structuredData={structuredData}
                pageType="blogDetail"
                dynamicData={{}}
                ogSection={category?.name || 'VR Industry'}
                ogTags={Array.isArray(post.tags) ? post.tags : ['VR', 'Meta Quest', 'App Store Optimization']}
            />

            <div className="page-content blog-detail-container">
                {previewData && (
                    // top matches the real fixed-header height (--topbar-height, 80px) so this
                    // sticky banner clears the topbar instead of sitting partly behind it —
                    // was hardcoded to the stale 70px header-height value.
                    <div className="bg-warning text-dark text-center py-2 mb-4 sticky-top border-bottom border-dark" style={{ top: '80px', zIndex: 1000 }}>
                        <strong>PREVIEW MODE</strong> - Showing unpublished content
                    </div>
                )}
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={8}>
                            <Card>
                                <CardBody>
                                    {/* Title */}
                                    <h1 className="mb-4">{post.title}</h1>
                                    <p className="blog-publish-date">
                                        {publishedAt ? `Published on ${formatDate(publishedAt)}` : 'Draft'}
                                    </p>

                                    {/* Featured Image */}
                                    {getImageUrl(post) && (
                                        <div className="blog-featured-image">
                                            <img
                                                src={getImageUrl(post)}
                                                alt={post.title}
                                                style={{ width: '100%', borderRadius: '12px', marginBottom: '24px' }}
                                            />
                                        </div>
                                    )}

                                    {/* Post Meta */}
                                    <div className="mb-4 blog-meta">
                                        {author?.name && (
                                            <span className="text-muted">
                                                By {author.name}
                                            </span>
                                        )}
                                        {category?.name && (
                                            <span className="ms-3">
                                                <span className="badge bg-primary">
                                                    {category.name}
                                                </span>
                                            </span>
                                        )}
                                    </div>

                                    {/* Description / Lead */}
                                    {post.description && (
                                        <div className="blog-description mb-4">
                                            <p className="lead">{post.description}</p>
                                        </div>
                                    )}

                                    {/* Content Rendering */}
                                    <div className="blog-content">
                                        {Array.isArray(post.blocks) && post.blocks.length > 0 ? (
                                            post.blocks.map((block, index) => blockRenderer(block, index))
                                        ) : post.content ? (
                                            typeof post.content === 'string' && post.content.startsWith('<') ? (
                                                <div dangerouslySetInnerHTML={{ __html: post.content }} />
                                            ) : (
                                                <ReactMarkdown>{post.content}</ReactMarkdown>
                                            )
                                        ) : (
                                            <p>No content available.</p>
                                        )}
                                    </div>

                                    {/* Tags */}
                                    {Array.isArray(post.tags) && post.tags.length > 0 && (
                                        <div className="mt-5 pt-4 border-top blog-tags">
                                            <h6 className="mb-3">Tags:</h6>
                                            <div className="d-flex flex-wrap gap-2">
                                                {post.tags.map((tag, index) => (
                                                    <span key={index} className="badge bg-light text-dark">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Latest Blogs */}
                                    {recentPosts.length > 0 && (
                                        <div className="mt-5 pt-4 border-top">
                                            <h5 className="mb-4">Latest Blogs</h5>
                                            <Row>
                                                {recentPosts.map((related) => {
                                                    const relatedCategory = related.category || {}
                                                    const relatedSlug = generateSlug(related.title || '')
                                                    const relatedImg = getImageUrl(related)
                                                    const relatedDate = related.published_at || related.publishedAt || related.created_at
                                                    return (
                                                        <Col md={4} key={related.id} className="mb-3">
                                                            <Link
                                                                to={`/blog/${relatedSlug}`}
                                                                style={{ textDecoration: 'none' }}
                                                                onClick={() => trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_POST_SELECTED, {
                                                                    post_title: related.title,
                                                                    post_slug: relatedSlug,
                                                                    category: relatedCategory?.name || relatedCategory,
                                                                    source_surface: "blog_detail_latest_blogs",
                                                                })}
                                                            >
                                                                <Card className="h-100 blog-card" style={{ cursor: 'pointer' }}>
                                                                    {relatedImg && (
                                                                        <img
                                                                            src={relatedImg}
                                                                            alt={related.title}
                                                                            style={{
                                                                                width: '100%',
                                                                                height: 'auto',
                                                                                display: 'block',
                                                                                borderRadius: '8px 8px 0 0',
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <CardBody className="d-flex flex-column p-3">
                                                                        <div className="mb-1 d-flex flex-wrap align-items-center gap-2">
                                                                            {relatedDate && (
                                                                                <small className="text-muted">
                                                                                    {formatDate(relatedDate)}
                                                                                </small>
                                                                            )}
                                                                            {relatedCategory?.name && (
                                                                                <span className="badge category-tag">
                                                                                    {relatedCategory.name}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <h6 className="card-title text-white mb-2" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                                                            {related.title}
                                                                        </h6>
                                                                        <p className="text-muted mb-0" style={{ fontSize: '13px', flexGrow: 1 }}>
                                                                            {getExcerpt(related)}
                                                                        </p>
                                                                    </CardBody>
                                                                </Card>
                                                            </Link>
                                                        </Col>
                                                    )
                                                })}
                                            </Row>
                                            <div className="mt-3">
                                                <Link to="/blog" className="btn btn-outline-primary btn-sm">
                                                    ← View All Posts
                                                </Link>
                                            </div>
                                        </div>
                                    )}

                                    {/* Fallback navigation if no recent posts loaded */}
                                    {recentPosts.length === 0 && (
                                        <div className="mt-5 pt-4 border-top">
                                            <Link to="/blog" className="btn btn-primary">
                                                ← Back to All Posts
                                            </Link>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default BlogDetail
